import React, { useEffect, useRef, useState } from "react";
import { buildGmailThreadUrlFromThreadId } from "../sourceNavigationPlan";

/**
 * Destinations that can be opened after a send.
 *
 * Gmail uses the stored message id as a `#all/<id>` deep link. Slack uses the
 * permalink returned by the send API. Channels without a URL are omitted.
 *
 * @example
 * viewChannelsFromSent({
 *   gmail: { messageId: "18abc0d1" },
 *   slack: { permalink: "https://x.slack.com/archives/C1/p1" },
 * });
 * // [{ id: "gmail", label: "Gmail", href: "https://mail.google.com/..." },
 * //  { id: "slack", label: "Slack", href: "https://x.slack.com/..." }]
 */
export const viewChannelsFromSent = ({ gmail, slack } = {}) => {
  const channels = [];
  const messageId = String(gmail?.messageId || "").trim();
  if (messageId) {
    channels.push({
      id: "gmail",
      label: "Gmail",
      href: buildGmailThreadUrlFromThreadId(
        "https://mail.google.com/mail/u/0/",
        messageId
      ),
    });
  }
  const permalink = String(slack?.permalink || "").trim();
  if (permalink) {
    channels.push({ id: "slack", label: "Slack", href: permalink });
  }
  return channels.filter((channel) => channel.href);
};

/**
 * One "View" control that opens sent destinations instead of a button per medium.
 *
 * Mirrors ``SendChannelMenu``: the toolbar stays one button as channels grow.
 * Clicking a destination opens it in a new tab.
 *
 * @example
 * <ViewChannelMenu
 *   destinations={viewChannelsFromSent({ slack: { permalink: "https://..." } })}
 * />
 * // Toolbar shows "View ▾" → Slack
 */
const ViewChannelMenu = ({ iconOnly = false, destinations = [] }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  if (destinations.length === 0) return null;

  return (
    <div className="ai-draft-expanded__send-menu" ref={rootRef}>
      <button
        type="button"
        className={`ai-crm-expanded__view-btn ai-draft-expanded__status-primary${
          iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
        }`}
        onClick={() => setOpen((current) => !current)}
        title="Open a sent copy of this message"
        aria-label="View"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {iconOnly ? (
          <span className="ai-icon-action-btn__glyph" aria-hidden="true">
            ↗
          </span>
        ) : (
          <span className="ai-text-action-btn__label">View ▾</span>
        )}
      </button>
      {open ? (
        <ul className="ai-draft-expanded__send-menu-list" role="menu">
          {destinations.map((channel) => (
            <li key={channel.id} role="none">
              <a
                role="menuitem"
                className="ai-draft-expanded__send-menu-item"
                href={channel.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                {channel.label}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default ViewChannelMenu;
